import cn from 'classnames'
import {AnimatePresence, m, useReducedMotion} from 'framer-motion'
import _ from 'lodash'
import {ButtonHTMLAttributes, FC, forwardRef, ReactNode, Ref, useState} from 'react'

import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {MIN_TARGET_SIZE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {CheckCircleOutlineIcon} from '../icons/CheckCircleOutlineIcon'
import {WarningIcon} from '../icons/WarningIcon'
import {LoadingDots} from '../loading/LoadingDots'

export const BUTTON_BORDER_RADIUS = 8

export enum ButtonStatus {
  idle = 'idle',
  submitting = 'submitting',
  success = 'success',
  error = 'error',
}

const slideInLeft = {
  initial: {x: '-100%', opacity: 0},
  animate: {x: 0, opacity: 1, transition: {duration: 0.25}},
  exit: {opacity: 0},
}

export type ButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  textColor?: string
  textAlign?: 'left' | 'right' | 'center'
  textVariant?: TextVariant
  fontWeight?: TextFontWeight
  ref?: Ref<HTMLButtonElement>
  hasBorder?: boolean
  isHoverable?: boolean
  isCentered?: boolean
  isIconAnimated?: boolean
  isInactive?: boolean
  children?: ReactNode
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type']
  disabled?: boolean
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick']
  isUnderlined?: boolean
  isCircular?: boolean
  isTextHiddenOnMobile?: boolean
  noWrap?: boolean
  isFocused?: boolean
  status?: ButtonStatus
  // Required for icon-only buttons
  ariaLabel?: string
  ariaExpanded?: boolean
  ariaControls?: string
}

export const Button: FC<ButtonProps> = forwardRef(
  (
    {
      type = 'submit',
      variant = ButtonVariant.default,
      size = ButtonSize.large,
      icon,
      iconPosition = 'right',
      disabled,
      fullWidth,
      textColor,
      textAlign = 'center',
      textVariant,
      fontWeight = TextFontWeight.semibold,
      hasBorder = true,
      isHoverable = false,
      isCentered = true,
      children,
      isIconAnimated = isHoverable,
      isInactive = false,
      onClick,
      isUnderlined,
      isCircular,
      isTextHiddenOnMobile = false,
      noWrap,
      isFocused = false,
      status: buttonStatus,
      ariaLabel,
      ariaExpanded,
      ariaControls,
    },
    ref: Ref<HTMLButtonElement>,
  ) => {
    const {t} = useTranslation()
    const {allDoneTitle, errorOccurredTitle} = useCommonContent()
    const [hovered, setHovered] = useState(false)
    const shouldReduceMotion = useReducedMotion()

    const className = useUniqueClassName('motion-button')
    const status = buttonStatus ?? ButtonStatus.idle

    const palette = theme.palette.button[variant]
    const backgroundColor = palette.main
    const hoverBackgroundColor = palette.dark
    const outlineColor = palette.outlineColor
    const contrastTextColor = textColor || palette.contrastText

    const isOutlined =
      hasBorder &&
      (variant === ButtonVariant.secondary ||
        variant === ButtonVariant.tertiary ||
        variant === ButtonVariant.darkGray)

    const isSubmitting = status === ButtonStatus.submitting
    const isSuccess = status === ButtonStatus.success
    const isError = status === ButtonStatus.error

    const isDisabled = disabled || isInactive || isSubmitting || isError

    const buttonTextVariant =
      textVariant ?? (size === ButtonSize.large ? TextVariant.subtitle14 : TextVariant.subtitle12)

    return (
      <>
        <m.button
          ref={ref}
          type={type}
          aria-label={ariaLabel}
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          className={cn(
            className,
            size,
            variant,
            iconPosition === 'left' && 'left',
            isDisabled && 'disabled',
            fullWidth && 'full-width',
            isOutlined && 'outlined',
            isHoverable && 'hoverable',
            isUnderlined && 'underline',
            isCircular && 'circular',
          )}
          onClick={onClick}
          disabled={isDisabled}
          onHoverStart={() => setHovered(true)}
          onHoverEnd={() => setHovered(false)}
          style={{
            backgroundColor: isInactive ? theme.palette.gray[100] : backgroundColor,
            color: contrastTextColor,
            boxShadow: isFocused ? `0px 0px 0px 4px ${theme.palette.gray[100]}` : 'none',
            border: isFocused
              ? `1px solid ${theme.palette.gray[900]}`
              : isOutlined
              ? `1px solid ${outlineColor}`
              : 'none',
          }}
          whileTap={{scale: 0.95, transition: {duration: 0.15, ease: 'easeOut'}}}
        >
          <m.div
            className={`${className}-bg`}
            initial={false}
            animate={{x: hovered && !isDisabled ? ['-101%', '0%'] : ['0%', '101%']}} // use 101% to avoid 1px artifacts caused by sub-pixel rounding in Framer motion
            transition={{duration: 0.15, ease: 'easeOut'}}
            style={{backgroundColor: hoverBackgroundColor}}
          />
          <m.div
            className={`${className}-status-bg`}
            initial={{x: '-101%'}}
            animate={{x: isSuccess || isError ? '0%' : '-101%'}}
            transition={{duration: 0.25, ease: 'easeOut'}}
            style={{
              backgroundColor: isSuccess
                ? theme.palette.success.outlineColor
                : theme.palette.error.outlineColor,
            }}
          />
          <AnimatePresence mode="popLayout">
            {status === ButtonStatus.idle && (
              <m.div
                key="idle-content"
                className={`${className}-content`}
                initial={false}
                exit={{x: ['0%', '101%'], transition: {duration: 0.3}}}
              >
                {children && (
                  <m.div className={cn(`${className}-text`, isTextHiddenOnMobile && 'hidden')}>
                    <Text
                      variant={buttonTextVariant}
                      fontWeight={fontWeight}
                      color={contrastTextColor}
                      className={cn('text-block', noWrap && 'space-nowrap')}
                    >
                      {children}
                    </Text>
                    {isHoverable && (
                      <m.div
                        className={`${className}-underline`}
                        initial={false}
                        animate={{width: hovered ? '100%' : 0}}
                        transition={{duration: shouldReduceMotion ? 0 : 0.15, ease: 'easeOut'}}
                        style={{backgroundColor: contrastTextColor}}
                      />
                    )}
                  </m.div>
                )}
                {icon && (
                  <m.div
                    className={`${className}-icon`}
                    animate={
                      hovered && !isDisabled && isIconAnimated
                        ? {x: iconPosition === 'left' ? -5 : 5}
                        : {x: 0}
                    }
                    transition={
                      hovered && !isDisabled && isIconAnimated
                        ? {
                            duration: 0.5,
                            ease: [
                              [0.42, 0, 1, 1],
                              [0, 0, 0.58, 1],
                            ],
                            repeat: Infinity,
                            repeatType: 'reverse',
                          }
                        : {duration: 0.2, ease: 'easeOut', repeat: 0}
                    }
                  >
                    {icon}
                  </m.div>
                )}
              </m.div>
            )}
            {isSubmitting && (
              <m.div
                key="loading"
                className={`${className}-status`}
                variants={slideInLeft}
                initial="initial"
                animate="animate"
                exit="exit"
                onUpdate={_.noop}
              >
                <LoadingDots color={theme.palette.background.default} />
              </m.div>
            )}
            {(isSuccess || isError) && (
              <m.div
                key="result"
                className={`${className}-status`}
                variants={slideInLeft}
                initial="initial"
                animate="animate"
                exit="exit"
                onUpdate={_.noop}
              >
                {isSuccess ? (
                  <CheckCircleOutlineIcon />
                ) : (
                  <WarningIcon color={theme.palette.error.contrastText} />
                )}
                <Text
                  variant={buttonTextVariant}
                  element="span"
                  fontWeight={fontWeight}
                  color={
                    isSuccess
                      ? theme.palette.success.contrastText
                      : theme.palette.error.contrastText
                  }
                >
                  {t(isSuccess ? allDoneTitle : errorOccurredTitle)}
                </Text>
              </m.div>
            )}
          </AnimatePresence>
        </m.button>

        <style jsx global>{`
          .${className} {
            position: relative;
            display: inline-flex;
            justify-content: ${isCentered ? 'center' : 'flex-start'};
            align-items: center;
            text-align: ${textAlign};
            height: 100%;
            padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
            border-radius: ${BUTTON_BORDER_RADIUS}px;
            cursor: pointer;
            overflow: hidden;
            gap: ${children ? theme.spacing[2] : 0};
          }
          .${className}:focus-visible {
            z-index: 1;
          }
          .${className}.disabled {
            cursor: default;
          }
          .${className}:not(.default) {
            min-height: 44px;
          }
          .${className}.default {
            padding: 0;
            min-width: ${MIN_TARGET_SIZE}px;
            min-height: ${MIN_TARGET_SIZE}px;
          }
          .${className}.full-width {
            width: 100%;
          }
          .${className}.medium:not(.default) {
            padding: 10px;
            min-height: 40px;
          }
          .${className}.small:not(.default) {
            min-height: 32px;
            border-radius: 5px;
            padding: ${theme.spacing[1.5]} 10px;
          }
          .${className}.left .${className}-content {
            flex-direction: row-reverse;
            justify-content: ${isCentered ? 'center' : 'flex-end'};
          }
          .${className}-bg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
          .${className}-status-bg {
            position: absolute;
            inset: 0;
          }
          .${className}-content {
            position: relative;
            display: flex;
            justify-content: ${isCentered ? 'center' : 'flex-start'};
            align-items: center;
            gap: ${children ? theme.spacing[2] : 0};
          }
          .${className}.full-width .${className}-content {
            width: 100%;
          }
          .${className}-text {
            position: relative;
          }
          .${className}.circular {
            padding: ${theme.spacing[2]};
            border-radius: 50%;
          }
          .${className}-icon {
            position: relative;
          }
          .${className}.hoverable {
            border-radius: 0;
          }
          .${className}-underline {
            position: absolute;
            bottom: 0;
            left: 0;
            height: 1px;
          }
          .${className}-status {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: ${theme.spacing[2]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .${className}-text.hidden {
              display: none;
            }
          }
        `}</style>
      </>
    )
  },
)
